import {useCallback, useEffect, useRef} from 'react'
import {useThree} from '@react-three/fiber'
import * as THREE from 'three'
import {
  EXPORT_SSAA_SCALE,
  EXPORT_SHADOW_MAP_SIZE,
  EXPORT_SHADOW_BIAS,
  EXPORT_SHADOW_NORMAL_BIAS,
  EXPORT_SHADOW_BLUR_SAMPLES,
} from '../constants/exportPresets.ts'
import {store} from '../store'
import {selectActiveScene} from './store/scenesSlice'
import {renderTextsToCanvas} from '../utils/textRenderer'
import {ensureTextFonts} from '../utils/ensureTextFonts'
import {waitForText3DFonts} from '../utils/text3dFontLoader'
import {preloadSpriteImages, mapSpritesToExportFrame, renderSpritesToCanvas} from '../utils/spriteRenderer'
import {applyPostProcessing} from '../utils/exportPostProcess'
import {DEFAULT_POST_PROCESS_PARAMS} from '../types/postProcessTypes'
import {isIosDevice} from '../utils/gpuCapabilities'
import type {ScreenText} from '../types/textTypes'
import type {ScreenSprite} from '../types/spriteTypes'
import {triggerBgRedraw} from './sceneBackgroundStore'

// iOS WebKit has a tight per-tab GPU budget. Bumping the shadow map past the
// viewport size (2048) or supersampling the framebuffer reliably exhausts it
// and kills the context mid-export. See `isIosDevice` for the full rationale.
const IOS_MAX_SHADOW_SIZE = 2048
const IOS_MAX_SSAA_SCALE = 1

// ── ExportHandler — renders scene at export resolution ────────────
// Must be placed INSIDE the R3F <Canvas> to access useThree().

/**
 * One slice of a carousel render. When provided, `renderExport` produces a
 * single tile at `(exportW × exportH)` using `camera.setViewOffset` so each
 * slice is rendered at native dimensions (no WebGL size limits) while the
 * projection matrix crops to the slice region of the full virtual canvas.
 */
export interface CarouselSliceParams {
  fullWidth: number
  fullHeight: number
  offsetX: number
}

export type RenderExportFn = (
  exportW: number,
  exportH: number,
  carousel?: CarouselSliceParams,
  // Reference canvas aspect (width/height) for mapping 2D texts/sprites from
  // viewport-relative coords into the export frame. 2D positions are stored
  // relative to the editor viewport, so the letterbox mapping depends on the
  // canvas aspect; passing the editor's aspect makes a server render reproduce
  // the client export exactly (the render canvas would otherwise differ, e.g.
  // puppeteer 1920×1080). Omitted → the live canvas aspect is used (client path).
  refAspect?: number,
) => Promise<HTMLCanvasElement | null>

interface ExportHandlerProps {
  onReady: (renderExport: RenderExportFn) => void
}

// ── Helpers ────────────────────────────────────────────────────────

/** Collect all shadow-casting directional lights */
const collectShadowLights = (scene: THREE.Scene) => {
  const lights: THREE.DirectionalLight[] = []
  scene.traverse((obj) => {
    if (obj instanceof THREE.DirectionalLight && obj.castShadow) {
      lights.push(obj)
    }
  })
  return lights
}

interface ShadowSnapshot {
  mapSizeW: number
  mapSizeH: number
  bias: number
  normalBias: number
  radius: number
  blurSamples: number
}

/**
 * Save shadow config and apply export-quality values. `maxShadowSize` clamps
 * `EXPORT_SHADOW_MAP_SIZE` against `gl.capabilities.maxTextureSize` — required
 * on mobile WebGL (iOS Safari/Chrome reports 4096) where an 8192² VSM map plus
 * its blur-pass allocation exceeds the per-tab GPU budget and crashes the page.
 */
const applyShadowExportConfig = (lights: THREE.DirectionalLight[], maxShadowSize: number): ShadowSnapshot[] => {
  const snapshots: ShadowSnapshot[] = lights.map((light) => ({
    mapSizeW: light.shadow.mapSize.x,
    mapSizeH: light.shadow.mapSize.y,
    bias: light.shadow.bias,
    normalBias: light.shadow.normalBias,
    radius: light.shadow.radius,
    blurSamples: light.shadow.blurSamples,
  }))

  const effectiveShadowSize = Math.min(EXPORT_SHADOW_MAP_SIZE, maxShadowSize)

  lights.forEach((light) => {
    // Scale radius proportionally so softness matches viewport appearance
    const scale = effectiveShadowSize / light.shadow.mapSize.x
    light.shadow.radius = light.shadow.radius * scale
    light.shadow.mapSize.set(effectiveShadowSize, effectiveShadowSize)
    light.shadow.bias = EXPORT_SHADOW_BIAS
    light.shadow.normalBias = EXPORT_SHADOW_NORMAL_BIAS
    light.shadow.blurSamples = EXPORT_SHADOW_BLUR_SAMPLES
    // Force full shadow map regeneration (map + VSM blur pass)
    if (light.shadow.map) {
      light.shadow.map.dispose()
      light.shadow.map = null as unknown as THREE.WebGLRenderTarget
    }
    const vsmShadow = light.shadow as unknown as {mapPass: THREE.WebGLRenderTarget | null}
    if (vsmShadow.mapPass) {
      vsmShadow.mapPass.dispose()
      vsmShadow.mapPass = null
    }
  })

  return snapshots
}

/** Restore original shadow config */
const restoreShadowConfig = (lights: THREE.DirectionalLight[], snapshots: ShadowSnapshot[]) => {
  lights.forEach((light, i) => {
    const snap = snapshots[i]
    light.shadow.mapSize.set(snap.mapSizeW, snap.mapSizeH)
    light.shadow.bias = snap.bias
    light.shadow.normalBias = snap.normalBias
    light.shadow.radius = snap.radius
    light.shadow.blurSamples = snap.blurSamples
    // Dispose both shadow map + VSM blur pass for clean regeneration
    if (light.shadow.map) {
      light.shadow.map.dispose()
      light.shadow.map = null as unknown as THREE.WebGLRenderTarget
    }
    const vsmShadow = light.shadow as unknown as {mapPass: THREE.WebGLRenderTarget | null}
    if (vsmShadow.mapPass) {
      vsmShadow.mapPass.dispose()
      vsmShadow.mapPass = null
    }
  })
}

/**
 * Map text positions from viewport-relative (0–1) to export-frame-relative (0–1).
 * Texts outside the export frame are filtered out.
 */
const mapTextsToExportFrame = (
  texts: ScreenText[],
  vpW: number,
  vpH: number,
  exW: number,
  exH: number,
): ScreenText[] => {
  const exAspect = exW / exH
  const vpAspect = vpW / vpH

  let frameW: number, frameH: number
  if (vpAspect > exAspect) {
    // Viewport is wider — frame fills height, narrower width
    frameH = vpH
    frameW = vpH * exAspect
  } else {
    // Viewport is taller — frame fills width, shorter height
    frameW = vpW
    frameH = vpW / exAspect
  }

  const offsetX = (vpW - frameW) / 2
  const offsetY = (vpH - frameH) / 2

  return texts
    .map((t) => {
      const px = t.x * vpW
      const py = t.y * vpH
      const fx = (px - offsetX) / frameW
      const fy = (py - offsetY) / frameH
      return {...t, x: fx, y: fy}
    })
    .filter((t) => t.x >= -0.5 && t.x <= 1.5 && t.y >= -0.5 && t.y <= 1.5)
}

// ── Component ─────────────────────────────────────────────────────

const ExportHandler = ({onReady}: ExportHandlerProps) => {
  const {gl, scene, camera} = useThree()
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null)

  useEffect(() => {
    cameraRef.current = camera as THREE.PerspectiveCamera
  }, [camera])

  // ── Full-resolution export render (SSAA + quality downscale) ────
  // Renders to the canvas at 2× size (preserving tone mapping + sRGB)
  // then downscales to target resolution with high-quality interpolation.
  //
  // When `carousel` is provided this renders ONE slice of the virtual full
  // canvas using `camera.setViewOffset` — the GL canvas stays at the slice
  // dimensions (no WebGL MAX_TEXTURE_SIZE / MAX_RENDERBUFFER_SIZE issues at
  // count ≥ 3) and the projection matrix crops to the slice region. The
  // camera aspect is set to the full carousel aspect so the projection is
  // identical to what a single wide render would produce.
  const renderExport = useCallback(
    async (
      exportW: number,
      exportH: number,
      carousel?: CarouselSliceParams,
      refAspect?: number,
    ): Promise<HTMLCanvasElement | null> => {
      const cam = cameraRef.current
      if (!cam) return null

      // 3D text typefaces load lazily — wait until every mounted 3D text has
      // its font parsed and committed so the GL renders below can't miss one.
      await waitForText3DFonts()

      // Supersampled dimensions — slice-sized for carousel, full for single.
      // EXPORT_SSAA_SCALE is a TARGET; we degrade if the GPU can't allocate a
      // buffer that large. Pre-clamp against maxTextureSize, then post-verify
      // drawingBufferWidth/Height after setSize since drivers can silently
      // clamp MSAA renderbuffers below maxTextureSize (without this guard,
      // exports ≳3000 px render into the lower-left of an oversized canvas).
      // On iOS, skip supersampling outright — the MSAA buffer alone has been
      // observed to crash the tab when combined with a bumped shadow map.
      const ios = isIosDevice()
      let ssaaScale = ios ? IOS_MAX_SSAA_SCALE : EXPORT_SSAA_SCALE
      const maxTexSize = gl.capabilities.maxTextureSize
      const longest = Math.max(exportW, exportH)
      if (longest * ssaaScale > maxTexSize) {
        ssaaScale = Math.max(1, maxTexSize / longest)
      }
      let ssW = Math.round(exportW * ssaaScale)
      let ssH = Math.round(exportH * ssaaScale)

      // Save current renderer state
      const prevSize = gl.getSize(new THREE.Vector2())
      const vpW = prevSize.x
      const vpH = prevSize.y
      const prevPixelRatio = gl.getPixelRatio()
      const prevAspect = cam.aspect

      // Bump shadow maps for export — clamped to maxTextureSize for mobile GPUs.
      // On iOS, clamp further to the viewport size: maxTextureSize reads 8192+
      // on newer iPhones but VSM at that size (×N shadow lights × map+blur pass)
      // crosses the per-tab GPU budget and kills the context.
      const shadowLights = collectShadowLights(scene)
      const maxShadowSize = ios
        ? Math.min(IOS_MAX_SHADOW_SIZE, gl.capabilities.maxTextureSize)
        : gl.capabilities.maxTextureSize
      const shadowSnapshots = applyShadowExportConfig(shadowLights, maxShadowSize)

      // Camera aspect is the FULL (carousel) frame so geometry projects the same
      // as a single wide render would; `setViewOffset` then crops to the slice.
      const aspectW = carousel ? carousel.fullWidth : exportW
      const aspectH = carousel ? carousel.fullHeight : exportH

      gl.setPixelRatio(1)
      gl.setSize(ssW, ssH, false)

      // Post-resize verification: if the driver clamped below what we asked
      // for, rescale ssW/ssH to match the real drawing buffer so subsequent
      // drawImage reads from the actually-rendered region.
      const glCtx = gl.getContext()
      if (glCtx.drawingBufferWidth < ssW || glCtx.drawingBufferHeight < ssH) {
        const fitScale = Math.min(glCtx.drawingBufferWidth / exportW, glCtx.drawingBufferHeight / exportH)
        ssaaScale = Math.max(1, fitScale)
        ssW = Math.round(exportW * ssaaScale)
        ssH = Math.round(exportH * ssaaScale)
        gl.setSize(ssW, ssH, false)
      }

      cam.aspect = aspectW / aspectH
      if (carousel) {
        cam.setViewOffset(carousel.fullWidth, carousel.fullHeight, carousel.offsetX, 0, exportW, exportH)
      } else {
        cam.clearViewOffset()
      }
      cam.updateProjectionMatrix()
      // Rebuild scene.background to match what this render needs.
      // For non-carousel: redraw at the export aspect (the texture fills the
      // slice viewport directly). For carousel: redraw onto a slice-sized
      // canvas that contains only this slice's portion of the wide layout —
      // `scene.background` is a screen-space quad and would otherwise stretch
      // a full-carousel texture into the narrow slice viewport, deforming the
      // background image.
      if (carousel) {
        triggerBgRedraw(exportW, exportH, {
          fullWidth: carousel.fullWidth,
          fullHeight: carousel.fullHeight,
          offsetX: carousel.offsetX,
          offsetY: 0,
        })
      } else {
        triggerBgRedraw(exportW, exportH)
      }
      gl.render(scene, cam)

      // Copy supersampled render from canvas
      const ssCanvas = document.createElement('canvas')
      ssCanvas.width = ssW
      ssCanvas.height = ssH
      ssCanvas.getContext('2d')!.drawImage(gl.domElement, 0, 0, ssW, ssH)

      // Downscale to target resolution with high-quality interpolation
      const resultCanvas = document.createElement('canvas')
      resultCanvas.width = exportW
      resultCanvas.height = exportH
      const resultCtx = resultCanvas.getContext('2d')!
      resultCtx.imageSmoothingEnabled = true
      resultCtx.imageSmoothingQuality = 'high'
      resultCtx.drawImage(ssCanvas, 0, 0, exportW, exportH)

      // Restore renderer state
      gl.setPixelRatio(prevPixelRatio)
      gl.setSize(prevSize.x, prevSize.y, false)
      cam.aspect = prevAspect
      if (carousel) cam.clearViewOffset()
      cam.updateProjectionMatrix()

      // Restore shadow config and rebuild scene.background at the editor aspect.
      restoreShadowConfig(shadowLights, shadowSnapshots)
      triggerBgRedraw(vpW, vpH)
      gl.render(scene, cam)

      // 2D texts/sprites are stored relative to the viewport; the letterbox
      // mapping into the export frame depends on the canvas aspect. Use the
      // caller-supplied reference aspect (the client editor's) when present so a
      // server render matches the client export byte-for-byte; only the ratio
      // matters, so any (w,h) with that ratio works.
      const mapVpW = refAspect ? refAspect : vpW
      const mapVpH = refAspect ? 1 : vpH

      // Composite 2D sprites first so 2D text stays on top of imagery.
      const currentSprites: ScreenSprite[] = selectActiveScene(store.getState()).sprites2d
      const renderableSprites = currentSprites.filter((s) => Boolean(s.imageUrl))
      if (renderableSprites.length > 0) {
        const fullMappedS = mapSpritesToExportFrame(renderableSprites, mapVpW, mapVpH, aspectW, aspectH)
        // Translate each sprite's full-strip position into slice-local coords.
        // No center-window filter: a sprite spanning multiple frames must render
        // its overlapping part in EVERY slice it crosses. renderSpritesToCanvas
        // draws at x*exportW and the slice canvas clips anything off-frame, so
        // items fully outside this slice are a harmless no-op.
        const sliceSprites = carousel
          ? fullMappedS.map((s) => ({...s, x: (s.x * carousel.fullWidth - carousel.offsetX) / exportW}))
          : fullMappedS
        if (sliceSprites.length > 0) {
          const images = await preloadSpriteImages(sliceSprites)
          const spriteCanvas = renderSpritesToCanvas(sliceSprites, images, exportW, exportH)
          resultCtx.drawImage(spriteCanvas, 0, 0)
        }
      }

      // Composite 2D texts. For carousel slices: map texts to the FULL frame
      // (consistent layout across slices), then translate x into slice-local
      // coords. No center-window filter — a text spanning multiple frames must
      // render its overlapping part in EVERY slice it crosses; renderTextsToCanvas
      // draws at x*exportW and the slice canvas clips whatever falls off-frame.
      const currentTexts = selectActiveScene(store.getState()).texts
      if (currentTexts.length > 0) {
        const fullMapped = mapTextsToExportFrame(currentTexts, mapVpW, mapVpH, aspectW, aspectH)
        const sliceTexts = carousel
          ? fullMapped.map((t) => ({...t, x: (t.x * carousel.fullWidth - carousel.offsetX) / exportW}))
          : fullMapped
        if (sliceTexts.length > 0) {
          // Canvas fillText won't load fonts itself — ensure they're ready first so
          // server-side renders don't fall back to sans-serif.
          await ensureTextFonts(sliceTexts)
          const textCanvas = renderTextsToCanvas(sliceTexts, exportW, exportH)
          resultCtx.drawImage(textCanvas, 0, 0)
        }
      }

      // Apply post-processing pipeline
      return applyPostProcessing(resultCanvas, DEFAULT_POST_PROCESS_PARAMS)
    },
    [gl, scene],
  )

  useEffect(() => {
    onReady(renderExport)
  }, [renderExport, onReady])

  return null
}

export default ExportHandler
